import { datadogRum } from "@datadog/browser-rum";

export type PatcherFailureOperation =
  | "attribute-resolution"
  | "decrypt-old"
  | "decrypt-seald"
  | "encrypt"
  | "retrieve-seald-session";

export type PatcherFailureRecord = {
  errorMessage: string;
  errorName: string;
  errorStack?: string;
};

export type PatcherFieldFailure = PatcherFailureRecord & {
  field: string;
  operation: PatcherFailureOperation;
};

export type PatcherFieldFailureSummary = {
  count: number;
  examples: PatcherFieldFailure[];
  fields: string[];
  operationCounts: Partial<Record<PatcherFailureOperation, number>>;
};

export type PatcherReport = {
  fieldFailures?: PatcherFieldFailureSummary;
  outerError?: PatcherFailureRecord;
};

export type PatcherFailureCollector = {
  addFieldFailure: (failure: {
    error: unknown;
    field: string;
    operation: PatcherFailureOperation;
  }) => void;
  recordOuterError: (error: unknown) => void;
  toReport: () => PatcherReport | null;
};

export type PatcherRunContext = {
  mode: "decrypt" | "encrypt";
  operationName?: string;
};

// A handful of example field-failures is enough to diagnose grouping; more
// than this just inflates the RUM payload without adding triage signal.
const MAX_EXAMPLES = 5;

// Hard cap on any sanitized string (message or stack). Seald stacks are
// typically a few hundred chars; 1500 leaves room for a full stack plus a
// sanitized message while keeping the RUM payload small.
const MAX_SANITIZED_LENGTH = 1500;

const ENCRYPTED_MESSAGE_RE = /"encryptedMessage"\s*:\s*"[^"]*"/g;
const LONG_DATA_RE = /"data"\s*:\s*"[^"]{64,}"/g;
const UUID_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

export function sanitizeSealdErrorMessage(raw: string): string {
  const stripped = raw
    .replace(ENCRYPTED_MESSAGE_RE, '"encryptedMessage":"<redacted>"')
    .replace(LONG_DATA_RE, '"data":"<redacted>"')
    .replace(UUID_RE, "<uuid>");
  return stripped.length > MAX_SANITIZED_LENGTH
    ? `${stripped.slice(0, MAX_SANITIZED_LENGTH)}…`
    : stripped;
}

function toFailureRecord(error: unknown): PatcherFailureRecord {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  return {
    errorMessage: sanitizeSealdErrorMessage(errorObj.message ?? ""),
    errorName: errorObj.name || "Error",
    errorStack: errorObj.stack
      ? sanitizeSealdErrorMessage(errorObj.stack)
      : undefined,
  };
}

// The intention of this is to collect single RUM error for patcher run, instead of one RUM error per field failure
// previously we were getting upwards of 500k logs per week just in the receiver app
// this should make digestion of the logs much easier and reduce noise and cost
export function createPatcherFailureCollector(): PatcherFailureCollector {
  const fieldFailures: PatcherFieldFailure[] = [];
  let outerError: PatcherFailureRecord | null = null;

  return {
    addFieldFailure({ error, field, operation }) {
      fieldFailures.push({
        ...toFailureRecord(error),
        field,
        operation,
      });
    },
    recordOuterError(error) {
      outerError = toFailureRecord(error);
    },
    toReport() {
      if (!fieldFailures.length && !outerError) return null;

      const report: PatcherReport = {};

      if (fieldFailures.length) {
        const operationCounts: Partial<
          Record<PatcherFailureOperation, number>
        > = {};
        const fieldSet = new Set<string>();
        for (const failure of fieldFailures) {
          operationCounts[failure.operation] =
            (operationCounts[failure.operation] ?? 0) + 1;
          fieldSet.add(failure.field);
        }
        report.fieldFailures = {
          count: fieldFailures.length,
          examples: fieldFailures.slice(0, MAX_EXAMPLES),
          fields: Array.from(fieldSet).sort((a, b) => a.localeCompare(b)),
          operationCounts,
        };
      }

      if (outerError) {
        report.outerError = outerError;
      }

      return report;
    },
  };
}

/**
 * Emit a single RUM error representing one patcher run. If an outer error is
 * present, its original name and stack are preserved so Datadog RUM shows the
 * real crash location; per-field failures are attached as structured context.
 * If only per-field failures are present, a synthetic aggregate Error is
 * constructed so the event is groupable.
 */
export function reportPatcherRun(
  report: PatcherReport,
  context: PatcherRunContext,
): void {
  const patcherContext: Record<string, unknown> = { ...context };

  if (report.fieldFailures) {
    patcherContext.fieldFailures = report.fieldFailures;
  }
  if (report.outerError) {
    patcherContext.outerError = report.outerError;
  }

  let primary: Error;
  if (report.outerError) {
    patcherContext.phase = report.fieldFailures
      ? "outer-catch-with-field-failures"
      : "outer-catch";
    primary = new Error(report.outerError.errorMessage);
    primary.name = report.outerError.errorName;
    if (report.outerError.errorStack) {
      primary.stack = report.outerError.errorStack;
    }
  } else {
    const summary = report.fieldFailures!;
    patcherContext.phase = "per-field-aggregate";
    primary = new Error(
      `Seald patcher: ${summary.count} field operation(s) failed during one ${context.mode} run`,
    );
    primary.name = "SealdPatcherAggregateError";
  }

  datadogRum.addError(primary, { patcher: patcherContext });
}
